<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useConversationKanban } from 'dashboard/composables/useConversationKanban';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import SidebarInfoCard from '../contact/SidebarInfoCard.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { kanbans, currentKanbanStage, moveToStage } = useConversationKanban();
const currentChat = useMapGetter('getSelectedChat');
const getKanbanById = useMapGetter('kanbans/getKanbanById');

const showDropdown = ref(false);

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};
const closeDropdown = () => {
  showDropdown.value = false;
};

const selectStage = stage => {
  moveToStage(stage);
  closeDropdown();
};

const clearStage = () => {
  moveToStage(null);
  closeDropdown();
};

// The conversation payload only carries the stage (id/name/color/kanban_id), not the parent
// board's name — resolved here from the kanbans Vuex module, already populated by the app
// sidebar on mount (see Sidebar.vue's `kanbans/get` dispatch), no extra API call needed.
const currentKanbanBoard = computed(() => {
  if (!currentKanbanStage.value) return null;
  return getKanbanById.value(currentKanbanStage.value.kanban_id);
});

const openKanbanBoard = () => {
  if (!currentKanbanStage.value) return;
  router.push(
    accountScopedRoute(
      'kanban_dashboard_index',
      { kanbanId: currentKanbanStage.value.kanban_id },
      { conversation_id: currentChat.value.id }
    )
  );
};
</script>

<template>
  <SidebarInfoCard
    :title="t('CONVERSATION_SIDEBAR.KANBAN.LABEL')"
    icon="i-lucide-kanban-square"
  >
    <div v-on-clickaway="closeDropdown" class="relative w-full">
      <button
        class="flex items-center w-full gap-2 px-3 py-2 text-sm transition-colors border rounded-lg border-n-weak bg-n-solid-1 hover:border-n-iris-8"
        @click="toggleDropdown"
      >
        <span
          v-if="currentKanbanStage"
          class="rounded-full size-2.5 shrink-0"
          :style="{ backgroundColor: currentKanbanStage.color }"
        />
        <i
          v-else
          class="text-n-slate-10 i-lucide-kanban-square size-3.5 shrink-0"
        />
        <span
          v-if="currentKanbanStage"
          class="flex-1 min-w-0 text-left truncate"
        >
          <span class="text-n-slate-12">{{ currentKanbanStage.name }}</span>
          <span v-if="currentKanbanBoard" class="ml-1 text-n-slate-10">{{
            `· ${currentKanbanBoard.name}`
          }}</span>
        </span>
        <span v-else class="flex-1 text-left truncate text-n-slate-12">
          {{ t('CONVERSATION_SIDEBAR.KANBAN.EMPTY') }}
        </span>
        <i class="text-n-slate-10 i-lucide-chevron-down size-3.5 shrink-0" />
      </button>

      <div
        v-if="showDropdown"
        class="absolute z-[9999] w-full mt-1 border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] border-n-strong shadow-lg p-2 max-h-64 overflow-y-auto"
      >
        <button
          v-if="currentKanbanStage"
          class="flex items-center w-full gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2 text-xs text-n-ruby-11"
          @click="clearStage"
        >
          <i class="i-lucide-x size-3.5" />
          {{ t('CONVERSATION_SIDEBAR.KANBAN.REMOVE') }}
        </button>

        <div v-if="!kanbans.length" class="px-2 py-1.5 text-xs text-n-slate-10">
          {{ t('CONVERSATION_SIDEBAR.KANBAN.NO_BOARDS') }}
        </div>

        <div v-for="kanban in kanbans" :key="kanban.id" class="mb-1">
          <div
            class="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-n-slate-10"
          >
            {{ kanban.name }}
          </div>
          <button
            v-for="stage in kanban.kanban_stages"
            :key="stage.id"
            class="flex items-center w-full gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2 text-sm"
            :class="{ 'bg-n-alpha-1': currentKanbanStage?.id === stage.id }"
            @click="selectStage(stage)"
          >
            <span
              class="rounded-full size-2 shrink-0"
              :style="{ backgroundColor: stage.color }"
            />
            <span class="truncate text-n-slate-12">{{ stage.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <button
      v-if="currentKanbanStage"
      class="flex items-center justify-center w-full gap-1.5 py-1.5 mt-2 text-xs font-medium transition-colors rounded-lg text-n-slate-11 hover:text-n-iris-11 hover:bg-n-alpha-1"
      :title="currentKanbanBoard?.name"
      @click="openKanbanBoard"
    >
      <i class="i-lucide-external-link size-3.5" />
      {{ t('CONVERSATION_SIDEBAR.KANBAN.OPEN_BOARD') }}
    </button>
  </SidebarInfoCard>
</template>
